import {
  ConditionalPaginationProps,
  CountlessOptions,
  decodeBase64PageCursor,
  encodeBase64PageCursor,
  useElementSize,
  WbButton,
  useResponsiveMobile,
  WbCard,
  WbDivider,
  WbDockableGroup,
  WbDockablePanel,
  WbDockableResizeHandle,
  WbDrawer,
  WbNoMatchingData,
  WbSearch,
  WbTablePagination,
} from '@agilelab/plugin-wb-platform';
import { snakeCaseToTitleCase } from '@agilelab/plugin-wb-platform-common';
import { OrderBySelector, SearchFilters } from '@agilelab/plugin-wb-search';
import {
  MARKETPLACE_FAVORITES,
  MARKETPLACE_PROJECTS_CONSUMABLE,
  MARKETPLACE_PROJECTS_DATA_LANDSCAPE,
  MARKETPLACE_PROJECTS_ENVIRONMENT,
  MARKETPLACE_PROJECTS_KIND,
  MARKETPLACE_PROJECTS_SYSTEM_URN,
  MARKETPLACE_PROJECTS_URN,
} from '@agilelab/plugin-wb-search-common';
import { ErrorPanel } from '@backstage/core-components';
import { Badge, Box, List, makeStyles, Typography } from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ViewTypeSelector } from '../components/ViewTypeSelector';
import { useSearchContext } from '../context/SearchContext';
import { MarketplaceSearchListItem } from '../SearchResult/MarketplaceSearchListItem';
import { SearchResultsLoader } from './loaders/SearchResultsLoader';
import { SearchResultsTableLoader } from './loaders/SearchResultsTableLoader';
import { SearchResultsTable } from './SearchResultsTable';

type SearchResultsContentProps = {};

const useStyles = makeStyles(
  theme => ({
    root: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      gap: theme.spacing(1),
      [theme.breakpoints.down('sm')]: {
        height: 'auto',
      },
    },
    resultContainer: {
      display: 'flex',
      flexDirection: 'column',
      paddingLeft: theme.spacing(1),
      paddingTop: theme.spacing(1.5),
      height: '100%',
      overflow: 'hidden',
    },
    divider: {
      marginBlock: theme.spacing(1.5),
    },
    resultsHeader: {
      paddingRight: theme.spacing(1),
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    resultsHeaderText: {
      color: theme.palette.secondary.dark,
      '& > b': {
        fontWeight: 500,
      },
      fontWeight: 400,
    },
    resultsHeaderBox: {
      display: 'flex',
      flexWrap: 'nowrap',
      flexDirection: 'row',
      justifyContent: 'flex-end',
      alignItems: 'center',
    },

    iconContainer: {
      border: `1px solid ${theme.palette.softened.primary}`,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '32px',
      borderRadius: '100%',
      borderStyle: 'dashed',
      // to separate the icon from the header and text
      margin: theme.spacing(2.5),
    },
    icon: {
      fontSize: '105px',
      color: theme.palette.softened.primary,
    },
    textNoDataContent: {
      textAlign: 'center',
      maxWidth: '400px',
    },
    cardStyle: {
      flex: 1,
      overflow: 'hidden',
      [theme.breakpoints.down('sm')]: {
        overflow: 'auto',
      },
    },

    mobileSearchRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    mobileFiltersButton: {
      borderRadius: theme.spacing(0.5),
      width: '36px',
      height: '36px',
      backgroundColor: theme.palette.white,
      minWidth: '36px',
      padding: 0,
    },
    dialogHeader: {
      backgroundColor: theme.palette.grey[50],
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: theme.spacing(1, 1.5),
      borderBottom: `1px solid ${theme.palette.grey[200]}`,
    },
    dialogTitle: {
      color: theme.palette.secondary.dark,
    },
    dialogContent: {
      padding: theme.spacing(0),
    },
    dialogFooter: {
      width: '100%',
      display: 'flex',
      gap: theme.spacing(1),
      justifyContent: 'flex-end',
      [theme.breakpoints.down('xs')]: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
      },
    },
  }),
  { name: 'SearchResultsContent' },
);

export const SearchResultsContent = ({}: SearchResultsContentProps) => {
  const classes = useStyles();

  const resultsRef = useRef<HTMLDivElement>(null);
  const { clientHeight: resultsHeight } = useElementSize(resultsRef);

  const {
    view,
    setView,
    data: value,
    isLoading: loading,
    error,
    environment,
    limit,
    cursor,
    setLimit,
    setCursor,
    query,
    setQuery,
    filters,
    setFilters,
    setFavoriteFilter,
    setOrderByOption,
    orderByOption,
    favoriteFilter,
    filtersContext,
    filterStateValid,
  } = useSearchContext();

  const handleChangePage = (newPage: number) => {
    setCursor(encodeBase64PageCursor(newPage));
  };

  const handleChangeRowsPerPage = (newLimit: number) => {
    setLimit(newLimit);
  };

  const handleTablePaginationChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    handleChangePage(newPage);
  };

  const handleTablePaginationChangeRowsPerPage = (
    event: React.ChangeEvent<{ name?: string; value: unknown }>,
  ) => {
    handleChangeRowsPerPage(parseInt(String(event.target.value), 10));
  };

  const countlessOptions = useMemo<CountlessOptions>(
    () => ({
      hasNextPage: () => !!value?.nextPageCursor,
      hasPrevPage: () => !!value?.previousPageCursor,
    }),
    [value],
  );

  const currentPage = decodeBase64PageCursor(cursor);

  const numberOfResults = value?.numberOfResults;

  const isMobile = useResponsiveMobile();

  const [filtersDialogOpen, setFiltersDialogOpen] = useState(false);

  const activeFiltersCount = useMemo(() => {
    const state = filters ?? {};
    const ignoredLabels = new Set<string>([
      MARKETPLACE_PROJECTS_CONSUMABLE.label,
      MARKETPLACE_FAVORITES.label,
      MARKETPLACE_PROJECTS_ENVIRONMENT.label,
      MARKETPLACE_PROJECTS_DATA_LANDSCAPE.label,
    ]);

    const defaultValues: Record<string, unknown> = {
      [MARKETPLACE_PROJECTS_CONSUMABLE.label]: 'true',
      [MARKETPLACE_FAVORITES.label]: 'false',
    };

    return Object.entries(state).reduce((acc, [label, filterValue]) => {
      if (ignoredLabels.has(label)) return acc;
      if (filterValue === undefined || filterValue === null) return acc;

      const defaultValue = defaultValues[label];
      if (defaultValue !== undefined && filterValue === defaultValue)
        return acc;

      if (Array.isArray(filterValue)) {
        return filterValue.length > 0 ? acc + 1 : acc;
      }

      if (typeof filterValue === 'string') {
        return filterValue.trim() !== '' ? acc + 1 : acc;
      }

      if (typeof filterValue === 'boolean') {
        return filterValue ? acc + 1 : acc;
      }

      return acc + 1;
    }, 0);
  }, [filters]);

  const handleClearFilters = () => {
    setFavoriteFilter(false);
    setFilters(undefined);
  };

  useEffect(() => {
    if (isMobile) {
      setView('table');
    }
  }, [isMobile, setView]);

  const tablePagination: ConditionalPaginationProps =
    numberOfResults === undefined
      ? {
          count: -1,
          rowsPerPageOptions: [15, 25, 50],
          limit,
          onPageChange: handleChangePage,
          onRowsPerPageChange: handleChangeRowsPerPage,
          currentPage,
          countlessOptions: {
            hasNextPage: countlessOptions.hasNextPage,
            isFirstPage: () => !countlessOptions.hasPrevPage(),
          },
        }
      : {
          count: Number(numberOfResults),
          rowsPerPageOptions: [15, 25, 50],
          limit,
          onPageChange: handleChangePage,
          onRowsPerPageChange: handleChangeRowsPerPage,
          currentPage,
        };

  const renderResults = () => {
    if (
      loading ||
      filtersContext.availableValues.loading ||
      // If the filter state is currenlt not valid, it means it's being updated to a valid state so instead of showing its results we show the Loader until it's valid again
      filterStateValid === false
    )
      return view === 'table' ? (
        <SearchResultsTableLoader />
      ) : (
        <SearchResultsLoader />
      );
    if (error) return <ErrorPanel error={error as any} />;
    if (!value) return <ErrorPanel error={new Error('Something went wrong')} />;

    let resultsComponent = <WbNoMatchingData />;

    if (value.results.length && view === 'table')
      resultsComponent = (
        <SearchResultsTable rows={value.results} pagination={tablePagination} />
      );

    if (value.results.length && view === 'cards')
      resultsComponent = (
        <>
          <div style={{ flex: 1, overflowY: 'scroll' }}>
            <List>
              {value.results.map(r => (
                <MarketplaceSearchListItem
                  key={r.document.documentId}
                  searchResult={r}
                />
              ))}
            </List>
          </div>
          {!!tablePagination?.count && (
            <>
              <WbDivider orientation="horizontal" />
              <WbTablePagination
                rowsPerPageOptions={
                  tablePagination.rowsPerPageOptions || [15, 25, 50]
                }
                component="div"
                count={tablePagination.count}
                rowsPerPage={tablePagination.limit}
                nextIconButtonProps={
                  tablePagination.countlessOptions
                    ? {
                        disabled:
                          !tablePagination.countlessOptions.hasNextPage(),
                      }
                    : undefined
                }
                backIconButtonProps={
                  tablePagination.countlessOptions
                    ? {
                        disabled:
                          tablePagination.countlessOptions.isFirstPage(),
                      }
                    : undefined
                }
                page={
                  'currentPage' in tablePagination
                    ? tablePagination.currentPage
                    : 0
                }
                onPageChange={handleTablePaginationChangePage}
                onRowsPerPageChange={handleTablePaginationChangeRowsPerPage}
              />
            </>
          )}
        </>
      );

    return <>{resultsComponent}</>;
  };

  const hiddenFilters = [
    MARKETPLACE_PROJECTS_URN.label,
    MARKETPLACE_PROJECTS_SYSTEM_URN.label,
    MARKETPLACE_PROJECTS_ENVIRONMENT.label,
    MARKETPLACE_PROJECTS_DATA_LANDSCAPE.label,
    MARKETPLACE_PROJECTS_CONSUMABLE.label,
    MARKETPLACE_PROJECTS_KIND.label,
  ];

  return (
    <>
      <Box className={classes.root}>
        <Box className={classes.mobileSearchRow}>
          <WbSearch value={query} debounced onChange={setQuery} />
          {isMobile && (
            <>
              <Badge badgeContent={activeFiltersCount} color="primary">
                <WbButton
                  aria-label="open filters"
                  onClick={() => setFiltersDialogOpen(true)}
                  icon={<FilterListIcon />}
                  color="default"
                  variant="outlined"
                  className={classes.mobileFiltersButton}
                />
              </Badge>
              <WbDrawer
                open={filtersDialogOpen}
                setOpen={setFiltersDialogOpen}
                title="Filters"
                hideCloseButton
                actions={
                  <Box className={classes.dialogFooter}>
                    <WbButton
                      variant="outlined"
                      size="small"
                      color="default"
                      onClick={handleClearFilters}
                      label="Clear filters"
                    />
                    <WbButton
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={() => setFiltersDialogOpen(false)}
                      label="Close"
                    />
                  </Box>
                }
                size={100}
              >
                <SearchFilters
                  isMobile={isMobile}
                  context={filtersContext}
                  hiddenFilterLabels={hiddenFilters}
                  filters={filters ?? {}}
                  setFilters={setFilters}
                  modalAnchorEl={resultsRef.current}
                  modalHeight={resultsHeight}
                  setFavoriteFilter={setFavoriteFilter}
                  favoriteFilter={favoriteFilter}
                />
              </WbDrawer>
            </>
          )}
        </Box>
        <WbCard
          title={
            <Typography
              component="span"
              role="status"
              className={classes.resultsHeaderText}
            >
              <b>{value?.numberOfResults ?? ''} Results</b>{' '}
              {environment && environment.name ? (
                <>
                  in <b>{snakeCaseToTitleCase(environment.name)}</b>
                </>
              ) : (
                <b>List</b>
              )}
            </Typography>
          }
          actions={
            <Box className={classes.resultsHeaderBox}>
              <OrderBySelector
                onChange={setOrderByOption}
                orderByOption={orderByOption}
              />
              {!isMobile && (
                <>
                  <WbDivider
                    orientation="vertical"
                    style={{ marginLeft: '8px' }}
                  />
                  <ViewTypeSelector />
                </>
              )}
            </Box>
          }
          cardClassName={classes.cardStyle}
        >
          <WbDockableGroup direction="horizontal">
            <>
              <WbDockablePanel
                defaultSize={16}
                maxSize={50}
                minSize={0}
                style={{
                  padding: 0,
                  display: isMobile ? 'none' : 'flex',
                  flexDirection: 'column',
                }}
              >
                <SearchFilters
                  context={filtersContext}
                  hiddenFilterLabels={hiddenFilters}
                  filters={filters ?? {}}
                  setFilters={setFilters}
                  modalAnchorEl={resultsRef.current}
                  modalHeight={resultsHeight}
                  setFavoriteFilter={setFavoriteFilter}
                  favoriteFilter={favoriteFilter}
                />
              </WbDockablePanel>
              <WbDockableResizeHandle
                showIconOnHover
                divider
                direction="horizontal"
                style={{ display: isMobile ? 'none' : 'flex' }}
              />
            </>
            <WbDockablePanel defaultSize={84}>
              <div className={classes.resultContainer} ref={resultsRef}>
                {renderResults()}
              </div>
            </WbDockablePanel>
          </WbDockableGroup>
        </WbCard>
      </Box>
    </>
  );
};
